import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ShieldCheck, CheckCircle2, Loader2 } from 'lucide-react';
import SEOHead, { generateBreadcrumbSchema } from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REQUEST_TYPES = [
  { value: 'access', label: 'Access — send me a copy of the personal data you have about me' },
  { value: 'deletion', label: 'Deletion — delete my personal data ("right to be forgotten")' },
  { value: 'export', label: 'Export — give me my data in a portable, machine-readable format' },
  { value: 'correction', label: 'Correction — fix inaccurate personal data' },
  { value: 'opt_out', label: 'Opt out — stop selling/sharing my data (CCPA) or processing for marketing' },
];

const JURISDICTIONS = [
  { value: '', label: 'Not specified' },
  { value: 'EU/GDPR', label: 'European Union (GDPR)' },
  { value: 'UK/UKGDPR', label: 'United Kingdom (UK GDPR)' },
  { value: 'California/CCPA', label: 'California (CCPA / CPRA)' },
  { value: 'Other US', label: 'Other US state with privacy law' },
  { value: 'Other', label: 'Other / Worldwide' },
];

export default function PrivacyRequestMyData() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    email: '',
    request_type: 'access',
    jurisdiction: '',
    details: '',
    _hp_company: '',
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/privacy/dsar`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit request. Email support@theailedger.ai instead.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-white px-6 py-16">
        <div className="max-w-xl mx-auto bg-neutral-900/60 border border-white/10 rounded-2xl p-8 text-center" data-testid="dsar-success">
          <CheckCircle2 className="w-14 h-14 text-[#00F0FF] mx-auto mb-5" />
          <h1 className="text-2xl font-bold mb-3">Request received</h1>
          <p className="text-neutral-400 leading-relaxed mb-3">
            Our privacy team will respond within 30 days, as required by GDPR and CCPA. If we have
            your email on file, you&apos;ll receive a confirmation email shortly.
          </p>
          <p className="text-xs text-neutral-500 leading-relaxed">
            For security reasons, we don&apos;t confirm whether a specific email is in our system on
            this page — that would let attackers enumerate accounts.
          </p>
          <Link to="/" className="inline-block mt-6 text-[#00F0FF] hover:underline">
            ← Back to marketplace
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="dsar-page">
      <SEOHead
        title="Request my data (DSAR) — theailedger.ai"
        description="Exercise your GDPR and CCPA rights — request access, deletion, export, correction, or opt-out of your personal data on theailedger.ai."
        path="/privacy/request-my-data"
        keywords="GDPR DSAR, CCPA request, data subject access request, right to be forgotten, theailedger privacy"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            generateBreadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Privacy', path: '/privacy' },
              { name: 'Request my data', path: '/privacy/request-my-data' },
            ]),
          ],
        }}
      />

      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-[#00F0FF] font-bold tracking-tight">theailedger.ai</Link>
          <Link to="/" className="text-sm text-neutral-400 hover:text-white inline-flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" />
            Back
          </Link>
        </div>
      </header>

      <section className="max-w-2xl mx-auto px-6 pt-12 pb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 text-xs font-semibold mb-5">
          <ShieldCheck className="w-3.5 h-3.5" />
          GDPR · CCPA · UK GDPR
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">Request access to your data</h1>
        <p className="text-neutral-300 text-base leading-relaxed mb-2">
          You can ask us to access, delete, export, or correct the personal data we hold about you.
          We respond to verified requests within 30 days at no charge.
        </p>
        <p className="text-sm text-neutral-500 leading-relaxed">
          We may need to verify your identity (typically by replying to an email sent to the
          address you provide) before fulfilling a deletion or export request, to prevent abuse.
        </p>
      </section>

      <section className="max-w-2xl mx-auto px-6 pb-20">
        <form onSubmit={submit} className="bg-neutral-900/40 border border-white/10 rounded-2xl p-5 sm:p-6 space-y-4" data-testid="dsar-form">
          {/* Honeypot */}
          <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: 0, height: 0, overflow: 'hidden' }}>
            <label htmlFor="dsar_company">Company (leave blank)</label>
            <input id="dsar_company" name="company" tabIndex={-1} autoComplete="off"
                   value={form._hp_company} onChange={update('_hp_company')} />
          </div>

          <label className="block">
            <span className="block text-sm font-semibold mb-1.5">Your email *</span>
            <input required type="email" value={form.email} onChange={update('email')}
                   data-testid="dsar-email"
                   placeholder="you@example.com"
                   className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]" />
            <p className="text-xs text-neutral-500 mt-1">Use the email associated with your theailedger account.</p>
          </label>

          <label className="block">
            <span className="block text-sm font-semibold mb-1.5">What would you like us to do? *</span>
            <select required value={form.request_type} onChange={update('request_type')}
                    data-testid="dsar-type"
                    className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]">
              {REQUEST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="block text-sm font-semibold mb-1.5">Your jurisdiction (optional)</span>
            <select value={form.jurisdiction} onChange={update('jurisdiction')}
                    className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]">
              {JURISDICTIONS.map((j) => <option key={j.value} value={j.value}>{j.label}</option>)}
            </select>
            <p className="text-xs text-neutral-500 mt-1">Helps us prioritize the legal basis we apply to your request.</p>
          </label>

          <label className="block">
            <span className="block text-sm font-semibold mb-1.5">Additional context (optional)</span>
            <textarea rows={3} value={form.details} onChange={update('details')}
                      placeholder="e.g. additional emails you've used, specific data you want corrected, etc."
                      className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF] resize-none" />
          </label>

          <button type="submit" disabled={submitting}
                  data-testid="dsar-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-7 py-3 bg-[#00F0FF] text-black font-bold rounded-xl hover:bg-[#00F0FF]/90 transition disabled:opacity-50">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {submitting ? 'Submitting…' : 'Submit privacy request'}
          </button>
        </form>
      </section>
    </div>
  );
}
